/**
 * ChartMind phone microphone relay.
 *
 * WHY THIS EXISTS. Clinic desktop microphones are bad, so recording an encounter at the desk
 * is a wash. A phone has a better mic and can be held near the patient's mouth. This module
 * is the whole cross-device mechanism: one short-lived Firestore document that carries a
 * transcript from the clinician's phone to the desktop tab they are already signed in on.
 *
 * ⚠️ IT IS NOT A SYNC OF THE APP, OR EVEN OF A COMPONENT. Speech recognition runs in the
 * browser, so what has to cross between devices is text, and ChartMind has exactly one
 * injection point for text: session.recording.updateTranscript(). Every downstream step (DDx,
 * diagnostic plan, treatment, discharge, chart, clarifying questions) already reads
 * `recording.transcript`. So the entire feature is one document with a string on it. Resist
 * every temptation to widen this into general session mirroring — see CHARTMIND-PHONE-MIC.md.
 *
 * ⚠️ ONE WRITER PER FIELD. The phone owns `transcript` and `command`; the desktop owns
 * `clarifyingQuestions` and `remote`. Neither ever writes the other's field, and the security rules
 * enforce the shape. The moment both devices write one field this becomes a two-way merge
 * problem, and last-write-wins on clinical data loses clinical data. The single exception is
 * the desktop blanking `transcript` between rounds (clearRelayTranscript, rearmPhoneRelay),
 * which writes the empty string and never content — see the note on those functions.
 *
 * ⚠️ WHY THERE IS NO CALLABLE AND NO SECRET, unlike functions/src/auth/deviceLink.js. Both
 * ends here are the SAME signed-in clinician, so `ownerUid == request.auth.uid` is the entire
 * authorization and a real security rule is possible. That is what lets the desktop use
 * onSnapshot instead of the two-second poll the device-link halves are stuck with, and it is
 * why the QR carries only a pairing id. The id is a pointer, NOT a credential: signing a
 * phone in does not route a transcript, and routing a transcript does not sign a phone in.
 *
 * PHI. The document carries the transcript of the round being recorded RIGHT NOW, and nothing
 * else: everything said earlier in the encounter is held on the desktop, and the desktop
 * blanks this field the moment it has consumed a round. It is get-only by its owner, never
 * listable, and hard-expired at 2h with an hourly sweep
 * (functions/src/scheduled/chartMindPhoneCleanup.js) for the ones where a tab was closed
 * mid-encounter. A pairing outlives its rounds so the clinician scans once per page load; what
 * outlives them is an empty pointer.
 */

import firebase from "firebase/compat/app";
import { db } from "services/firebase";

const COLLECTION = "chartmindPhoneSessions";

/**
 * 2 hours, fixed and immutable once written.
 *
 * Deliberately NOT sliding (the phone pushing expiresAt forward on each flush): the rule would
 * then have to bound the extension instead of pinning the field, and an active phone could keep
 * a transcript alive in Firestore indefinitely. Fixed is one fewer thing to get right, and the
 * failure mode of a longer-than-2h encounter is a rejected write the phone surfaces as "please
 * continue on your computer".
 *
 * Nests inside the cascade documented in functions/src/scheduled/guestCleanup.js:
 *   device-link grant 2min  <  relay 2h  <  share code 24h  <  guest session 48h  <  anon 72h
 * Below the share code so a relay never outlives the redeemed patient record an encounter may
 * be running against. Widening this means re-checking that it still nests.
 */
export const PHONE_RELAY_TTL_MS = 2 * 60 * 60 * 1000;

/** Debounce on the phone's transcript flush: feels live on a speech pause. */
export const PHONE_RELAY_FLUSH_MS = 1000;

/** Ceiling on that debounce, so continuous talking still flushes. Bounds data loss too. */
export const PHONE_RELAY_MAX_WAIT_MS = 3000;

/**
 * How long the desktop waits for the phone to say ANYTHING after a stop request.
 *
 * This one covers a phone that is dead, backgrounded or offline — the alternative is the
 * desktop hanging on "stopping" forever. It is short because a live phone answers immediately:
 * the first thing it does on seeing `stopping` is write back, before it starts draining.
 */
export const PHONE_RELAY_STOP_TIMEOUT_MS = 5000;

/**
 * How long the desktop waits once the phone HAS answered.
 *
 * ⚠️ THESE TWO NUMBERS ARE NOT INTERCHANGEABLE, and collapsing them back into one loses
 * clinical data. Transcription moved server-side (useEncounterRecording), so stopping is no
 * longer instant: the phone has to wait for the segments already in flight to come back as
 * text, which it does on a 30s deadline of its own. Against a single 5s timeout the desktop
 * gave up mid-drain on any slow round trip, consumed the transcript as of the LAST FLUSH
 * BEFORE the stop, and deleted the document — so the tail of the encounter was silently
 * missing and the phone's final write failed with NOT_FOUND.
 *
 * So: 5s to prove the phone is alive, then this to let it finish. Must stay above the phone's
 * own drain deadline, or the desktop is back to cutting it off.
 */
export const PHONE_RELAY_STOP_FLUSH_MS = 35_000;

/**
 * ⚠️ THERE IS NO LONGER A GRACE WINDOW, AND ITS ABSENCE IS THE POINT.
 *
 * A pairing used to be torn down a fixed time after the phone stopped, so the clinician got a
 * fresh QR — and had to scan it again — every time they recorded a second encounter in the
 * same tab. A pairing is not a recording: it is a wire between this desktop tab and that
 * phone, and it now lasts as long as the tab holds an encounter, bounded by the document's own
 * two hour expiry. One scan per page load.
 *
 * What made that affordable is the round model below. The wire carries only the CURRENT
 * round's text: the desktop holds everything said before it (see usePhoneRecordingRelay's
 * round base) and blanks the wire the moment it has consumed a round. So between rounds the
 * document holds no transcript at all, which is a tighter PHI posture than the ten-minute
 * window it replaces, not a looser one. A pairing left behind by a closed tab is now an empty
 * pointer rather than an encounter waiting for the sweep.
 */

/** Terminal states the desktop is allowed to consume. */
export const PHONE_RELAY_STATUS = {
  WAITING: "waiting",
  // The phone has opened the pairing but not started recording. Exists so the desktop can
  // acknowledge a successful scan and, importantly, take the QR off screen: that image carries
  // a single-use sign-in grant, and there is no reason to keep displaying one after it is spent.
  CONNECTED: "connected",
  RECORDING: "recording",
  STOPPING: "stopping",
  STOPPED: "stopped",
};

const serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp();

function normalizeRelay(doc) {
  const data = doc.data() || {};
  return {
    pairingId: doc.id,
    ownerUid: data.ownerUid || null,
    status: data.status || PHONE_RELAY_STATUS.WAITING,
    transcript: typeof data.transcript === "string" ? data.transcript : "",
    clarifyingQuestions: Array.isArray(data.clarifyingQuestions)
      ? data.clarifyingQuestions
      : [],
    // Where the desktop is and what its Next button would do. Written by the desktop, rendered
    // by the phone, and never anything but the app's own step names — see writeRemoteState.
    remote: data.remote && typeof data.remote === "object" ? data.remote : null,
    // The phone asking the desktop to do something. One verb, carrying an id so the desktop can
    // tell a new press from the same press arriving in another snapshot.
    command: data.command && typeof data.command === "object" ? data.command : null,
    language: data.language || "en-US",
    phoneClaimedAt: data.phoneClaimedAt || null,
    expiresAt: typeof data.expiresAt === "number" ? data.expiresAt : 0,
  };
}

/**
 * Desktop: open a pairing. Returns the new pairing id, which goes in the QR.
 *
 * The document is created empty and `status: 'waiting'` — the rules require both, so a create
 * cannot smuggle transcript text onto an id nobody has claimed yet.
 */
export async function createPhoneRelay({ ownerUid, language = "en-US" }) {
  if (!ownerUid) throw new Error("ownerUid is required");

  const ref = db.collection(COLLECTION).doc();
  await ref.set({
    ownerUid,
    status: PHONE_RELAY_STATUS.WAITING,
    transcript: "",
    clarifyingQuestions: [],
    language,
    phoneClaimedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: Date.now() + PHONE_RELAY_TTL_MS,
  });
  return ref.id;
}

/**
 * Both devices: watch one pairing.
 *
 * `onNext` receives null when the document is gone (consumed by the desktop, cancelled, or
 * swept) — the phone reads that as "this recording ended on your computer".
 */
export function subscribePhoneRelay(pairingId, onNext, onError, { includeLocalWrites = false } = {}) {
  if (!pairingId) throw new Error("pairingId is required");

  return db
    .collection(COLLECTION)
    .doc(pairingId)
    .onSnapshot(
      (doc) => {
        /**
         * ⚠️ SKIPPING PENDING-WRITE SNAPSHOTS SILENTLY DROPS THE OTHER DEVICE'S CHANGES, and
         * that is not a theoretical hazard — it is what made the phone's step buttons stick.
         *
         * `hasPendingWrites` is true for EVERY snapshot of this document while any local write
         * of ours is unacknowledged, including the snapshots that carry the other device's
         * changes. So: the phone writes a command, the desktop acts and writes the new `remote`,
         * that update reaches the phone while its own write is still in flight — and the whole
         * snapshot is dropped. When the write is finally acked the document's contents are
         * identical to the snapshot that was dropped, so Firestore emits only a metadata change,
         * which a listener without includeMetadataChanges never sees. The new remote is gone for
         * good, and the phone sits on the previous step's label until something else happens to
         * change the document. Fast ack: fine. Slow ack, which is a phone on cell data in an
         * exam room: stuck. That is the "sometimes" in the bug report.
         *
         * So a caller that must not miss a change passes includeLocalWrites and takes its own
         * echo along with it. Both handlers are written to expect that — see the stop-answer
         * guard on the phone and the self-stop guard in usePhoneRecordingRelay, which are the
         * only two places where acting on our own write would mean anything.
         */
        if (!includeLocalWrites && doc.metadata.hasPendingWrites) return;
        onNext(doc.exists ? normalizeRelay(doc) : null);
      },
      (error) => onError?.(error),
    );
}

/** Phone: one-shot read, to check the code is live before offering to record. */
export async function getPhoneRelay(pairingId) {
  if (!pairingId) throw new Error("pairingId is required");
  const doc = await db.collection(COLLECTION).doc(pairingId).get();
  if (!doc.exists) return null;
  const relay = normalizeRelay(doc);
  // Expired and never-existed are the same answer to the caller, deliberately — the same
  // reasoning as deviceLink.js: a caller learns nothing about whether an id it guessed was
  // ever real. The sweep will collect the document itself.
  if (relay.expiresAt && relay.expiresAt < Date.now()) return null;
  return relay;
}

/**
 * Phone: say hello, without claiming the microphone yet.
 *
 * Only ever moves a pairing forward from `waiting`, so a phone reloading mid-encounter cannot
 * knock a live recording back to a lobby state.
 */
export async function connectPhoneRelay(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).update({
    status: PHONE_RELAY_STATUS.CONNECTED,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Phone: claim the pairing and start recording.
 *
 * ⚠️ THE CLAIM EMPTIES THE WIRE, AND IT HAS TO DO IT IN THE SAME WRITE. The transcript field
 * carries the CURRENT round only, so at the moment a round is claimed it is empty by
 * definition — this phone has just reset its recorder and its own last-flushed marker. The
 * desktop blanks it after consuming a round (clearRelayTranscript), but that is a second write
 * that can be lost or overwritten: a stop request that crossed the phone's own stop leaves the
 * previous round's text back on the document (see the stale-stop answer in
 * PhoneRecordingPage), and nothing clears it again.
 *
 * What that cost: the desktop stitches whatever is on the wire onto the round's base, so the
 * snapshot carrying this claim handed it the PREVIOUS round's text. Dictating into a note
 * section after an encounter round therefore pasted the encounter transcript into the section
 * for a second, until the first real flush replaced it. Clearing here rather than only on
 * consumption means a round cannot start on top of the last one's words, whatever happened to
 * the clear that was supposed to have run.
 */
export async function claimPhoneRelay(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).update({
    status: PHONE_RELAY_STATUS.RECORDING,
    transcript: "",
    phoneClaimedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Phone: flush the transcript.
 *
 * ⚠️ THE ROUND, NOT THE ENCOUNTER. This is the text spoken since the phone last tapped record,
 * and the desktop stitches it onto whatever came before (usePhoneRecordingRelay's round base).
 * The phone deliberately does not carry earlier rounds: it used to seed them back in from its
 * own memory, which lost the lot whenever the phone was reloaded mid-encounter and put the
 * whole encounter on the wire for as long as the pairing lived.
 *
 * The whole string every time, not deltas. A 20-minute encounter is ~18KB, nowhere near the
 * 1MiB document limit, and at a 3s ceiling that is under 400 writes — well under a cent per
 * encounter against the ~$0.03 of LLM spend an encounter already costs. Deltas would buy only
 * bandwidth, in exchange for sequence numbers, gap detection and reassembly, and a string is
 * the only form updateTranscript() can consume without a reducer.
 *
 * Pass `final: true` on stop. That is the ONLY write that sets `stopped`, and the desktop
 * consumes nothing else — see requestPhoneStop.
 */
export async function writePhoneTranscript(pairingId, transcript, { final = false } = {}) {
  return db.collection(COLLECTION).doc(pairingId).update({
    transcript: transcript || "",
    ...(final ? { status: PHONE_RELAY_STATUS.STOPPED } : {}),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Desktop: push the current clarifying questions down to the phone.
 *
 * The desktop is already polling for these, so it writes them rather than the phone running a
 * second useClarifyingQuestions — that would bill a second set of LLM calls for one encounter.
 */
export async function writeClarifyingQuestions(pairingId, questions) {
  return db.collection(COLLECTION).doc(pairingId).update({
    clarifyingQuestions: (questions || []).slice(0, 20),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Desktop: tell the phone where the encounter is and what Next would do.
 *
 * ⚠️ THIS IS A REMOTE, NOT A MIRROR, and the line between them is the whole reason the payload
 * is this small. What goes down is the app's own step names ("Diagnosis"), the Next button's
 * label ("Review Plan"), whether it can fire, and a fixed sentence saying what to do if it
 * cannot. No diagnoses, no transcript, no patient — nothing on this wire is about the person in
 * the room, which is what keeps the phone a screen you can leave face-up on a counter.
 *
 * The moment this grows a field that says what the model FOUND rather than what the app is
 * SHOWING, it has become session mirroring and needs the argument in CHARTMIND-PHONE-MIC.md
 * re-made from scratch.
 *
 * Fire and forget: the desktop's step is authoritative regardless, and a failed push costs a
 * stale label on the phone rather than anything clinical.
 */
export async function writeRemoteState(pairingId, remote) {
  return db.collection(COLLECTION).doc(pairingId).update({
    remote: buildRemoteState(remote),
    updatedAt: serverTimestamp(),
  });
}

/**
 * The exact shape that goes on the wire, in a fixed key order.
 *
 * Exported so the desktop can ask "is what is on the document what I last sent?" and compare
 * the two as strings. Both sides of that comparison have to be built here, or key order alone
 * makes every reading unequal — Firestore hands map fields back in its own order, not ours.
 */
export function buildRemoteState(remote) {
  return {
    stepLabel: String(remote?.stepLabel || ""),
    nextLabel: String(remote?.nextLabel || ""),
    nextEnabled: Boolean(remote?.nextEnabled),
    blockedReason: String(remote?.blockedReason || "").slice(0, 160),
    // The Record step's secondary action. Empty everywhere else, which is what takes it off
    // the phone: the phone renders a button per non-empty label and nothing more.
    skipLabel: String(remote?.skipLabel || ""),
    skipEnabled: Boolean(remote?.skipEnabled),
    // "step" is the ordinary remote; "dictate" means the desktop has a note section's editor
    // open and this phone's next round belongs in it. dictateLabel is the section's title, and
    // it is a STRING TO PRINT — the phone is not told which section it is and cannot pick one.
    mode: remote?.mode === "dictate" ? "dictate" : "step",
    dictateLabel: String(remote?.dictateLabel || "").slice(0, 80),
  };
}

/**
 * Phone: ask the desktop to do something.
 *
 * ⚠️ THE PHONE NEVER DECIDES WHETHER IT IS ALLOWED. It sends a verb; the desktop re-checks the
 * gate against its own state before acting, because the phone's copy of "can I press Next" is
 * always a snapshot old. A phone that pressed Next as the last diagnosis was deselected must not
 * be able to advance a step the desktop would refuse.
 *
 * The id is what makes this idempotent. Firestore replays snapshots — reconnects, a later write
 * to another field — and every one of them carries whatever is in `command`. Without an id the
 * desktop would advance a step again every time the document changed for an unrelated reason.
 */
export async function writePhoneCommand(pairingId, verb) {
  return db.collection(COLLECTION).doc(pairingId).update({
    command: {
      verb,
      // Unique per press rather than per document: two presses of the same button must look
      // different, and Date.now() alone repeats inside a millisecond.
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Desktop: blank the wire once a round has been consumed.
 *
 * ⚠️ THE DESKTOP WRITING `transcript` IS A DELIBERATE EXCEPTION to the one-writer-per-field
 * rule at the top of this file, and it does not reopen the merge problem that rule exists to
 * prevent: it only ever writes the empty string, only after the phone has reported `stopped`,
 * and never a word of content. It is a reset of the wire, not a second opinion about what is
 * on it.
 *
 * This is what lets a pairing outlive the round that used it. Without it the last encounter
 * would sit on the document for as long as the tab stayed open.
 */
export async function clearRelayTranscript(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).update({
    transcript: "",
    updatedAt: serverTimestamp(),
  });
}

/**
 * Desktop: point an existing pairing at a new encounter.
 *
 * The clinician pressed "Record on your phone" again on a phone that is already paired. The
 * code in their pocket is still good, so nothing is minted and nothing is scanned — the wire
 * is emptied and the pairing is put back in the lobby state, which the phone reads as "the
 * computer has started something new" and answers by returning to its ready screen.
 *
 * The clarifying questions go with it. They were asked about the last patient, and the phone
 * is the screen in the clinician's hand: leaving them there would put one encounter's questions
 * in front of the next one until the first poll of the new recording replaced them.
 */
export async function rearmPhoneRelay(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).update({
    status: PHONE_RELAY_STATUS.CONNECTED,
    transcript: "",
    clarifyingQuestions: [],
    // The desktop is back on the Record step, so last encounter's "Next: Generate Note" would
    // be wrong for a beat until the first push landed. Null reads as "nothing to offer yet".
    remote: null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Desktop: ask the phone to stop.
 *
 * ⚠️ THE DESKTOP MUST NOT WRITE `stopped` ITSELF. Its own write would echo back through its own
 * listener and it would consume-and-delete before the phone had flushed its last few seconds of
 * speech. So the desktop asks (`stopping`), the phone answers (`stopped` + a final flush), and
 * the desktop only ever consumes `stopped`. If the phone never answers within
 * PHONE_RELAY_STOP_TIMEOUT_MS the desktop takes whatever is on the document and deletes anyway.
 */
export async function requestPhoneStop(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).update({
    status: PHONE_RELAY_STATUS.STOPPING,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete the relay.
 *
 * Delete-AFTER-read here, deliberately unlike deviceLink.js's delete-before-mint. There the
 * payload is a bearer credential, so a live spent session is worse than a failed delete. Here
 * the payload is a transcript: a failed delete costs a document the sweep collects anyway,
 * whereas a failed read costs the encounter.
 */
export async function deletePhoneRelay(pairingId) {
  return db.collection(COLLECTION).doc(pairingId).delete();
}

/**
 * The URL that goes in the QR.
 *
 * A plain https deep link, so the phone's NATIVE camera opens it and there is no in-app scanner
 * to build — the same convention as buildDeviceLinkUrl and healthRecordsShareService's
 * buildQrPayload.
 *
 * ⚠️ TWO SEPARATE THINGS RIDE IN HERE, and conflating them is the mistake to avoid. `p` is the
 * pairing id: it says WHICH desktop encounter this phone feeds, and it is a pointer, not a
 * credential. `g`/`s` are an optional device-link grant: they say WHO the phone is, and while
 * unspent they are a credential. Signing a phone in does not route a transcript, and routing a
 * transcript does not sign a phone in, which is why both are needed and why they stay distinct.
 */
export function buildPhoneRecordingUrl(pairingId, grant = null) {
  const params = new URLSearchParams({ p: pairingId });
  if (grant?.grantId && grant?.grantSecret) {
    // The sign-in half, so a phone with no session does not have to type a password and an MFA
    // code in front of a patient. A phone that is already signed in ignores these outright, and
    // they are spent on first use either way — see createDeviceLinkGrant in deviceLinkService.
    params.set("g", grant.grantId);
    params.set("s", grant.grantSecret);
  }
  return `${window.location.origin}/chartmind-mic?${params.toString()}`;
}
